import apiClient from './apiClient';
import type { CycleRole } from './cyclesService';

export type PlanType = 'spe' | 'reprise' | 'generic';
/** R47: `DAYS_ONLY` — l'athlète donne ses jours, le coach décide du contenu. */
export type DayMode = 'ATHLETE' | 'DAYS_ONLY' | 'FIXED';

export interface PlanCompositionSegment {
  cycleId: string;
  name: string;
  orderIndex: number;
  weekCount: number | null;
  maxWeekNumber: number;
  /** Effective max weeks of this cycle in this plan (cap and format ranks applied). */
  weeks: number;
  role: CycleRole;
}

export interface Generability {
  generable: boolean;
  issueCount: number;
  dminWeeks: number;
  dmaxWeeks: number;
}

export interface CatalogIssue {
  code: string;
  cycleId?: string;
  planCycleOrder?: number;
  weekNumber?: number;
  cycleSessionId?: string;
  detail?: Record<string, unknown>;
}

/** v7: what names a plan — its race type, its optional format, its optional name. */
export interface PlanIdentity {
  id: string;
  /** As authored; null when the coach left it empty. */
  name: string | null;
  /** `name`, else « <race type> <format> ». */
  displayName: string;
  raceTypeId: number;
  raceTypeIdentifier: string;
  raceTypeLabel: string;
  /** Null for a Débutant plan. */
  formatId: string | null;
  formatIdentifier: string | null;
  formatSlug: string | null;
  formatLabel: string | null;
  /** False for a plan the v7 migration could not map: athletes cannot pick it until it is saved again. */
  isGeneratable: boolean;
}

export interface PlanListItem extends Generability, PlanIdentity {
  planType: PlanType;
  dayMode: DayMode;
  condensedMinWeeks: number | null;
  cyclesCount: number;
  totalWeeks: number;
  composition: PlanCompositionSegment[];
  isAvailable: boolean;
  updatedAt: string;
}

export interface PlanList {
  items: PlanListItem[];
  total: number;
}

export interface PlanKpis {
  total: number;
  published: number;
  drafts: number;
  activeUsers: number;
}

export interface PlanDetailCycle {
  id: string;
  cycleId: string;
  orderIndex: number;
  weekCount: number | null;
  effectiveMaxWeeks: number;
  name: string;
  role: CycleRole;
  minWeekNumber: number;
  maxWeekNumber: number;
  priorityByFormat: Record<string, number>;
  weekRanks: Record<number, number>;
  rankedWeeksForFormat: number;
}

export interface PlanSportVolume {
  sportId: string;
  min: number;
  max: number;
  default: number;
}

export interface PlanDetail extends Generability, PlanIdentity {
  planType: PlanType;
  dayMode: DayMode;
  condensedMinWeeks: number | null;
  cycles: PlanDetailCycle[];
  sportVolumes: PlanSportVolume[];
  totalWeeks: number;
  isAvailable: boolean;
  updatedAt: string;
  issues: CatalogIssue[];
}

export interface PlanCycleInput {
  cycleId: string;
  orderIndex: number;
  weekCount: number | null;
}

export interface PlanPayload {
  name: string | null;
  raceTypeId: number;
  /** Ignored (stored NULL) for a Débutant plan. */
  formatId: string | null;
  isAvailable: boolean;
  dayMode: DayMode;
  condensedMinWeeks: number | null;
  cycles: PlanCycleInput[];
  sportVolumes: PlanSportVolume[];
}

export interface PlanDeleteImpact {
  cyclesRemoved: number;
  followers: number;
  backupForFormats: string[];
}

export interface GenerationCheck extends Generability {
  issues: CatalogIssue[];
  planType: PlanType;
  dayMode: DayMode;
  formatCode: string;
  sports: { id: string; label: string }[];
  /** Non-PPG sports of the plan: the rows of the sessions-per-week table. */
  volumeSportIds: string[];
  sportVolumes: PlanSportVolume[];
  hasBricks: boolean;
  hasTests: boolean;
  hasTestCycle: boolean;
  hasRecoveryCycles: boolean;
  hasPpg: boolean;
  ppgSportId: string;
  condensedMinWeeks: number | null;
  cycleNames: Record<string, string>;
}

export interface PlanFilters {
  search?: string;
  format?: string;
  raceType?: string;
  planType?: string;
  page?: number;
  pageSize?: number;
}

export const getPlans = async (filters: PlanFilters): Promise<PlanList> => {
  const params: Record<string, string | number> = {};
  if (filters.search) params.search = filters.search;
  if (filters.format && filters.format !== 'all') params.format = filters.format;
  if (filters.raceType && filters.raceType !== 'all') params.raceType = filters.raceType;
  if (filters.planType && filters.planType !== 'all') params.planType = filters.planType;
  if (filters.page) params.page = filters.page;
  if (filters.pageSize) params.pageSize = filters.pageSize;
  const response = await apiClient.get('admin/plans', { params });
  return response.data as PlanList;
};

export const getPlanKpis = async (): Promise<PlanKpis> => {
  const response = await apiClient.get('admin/plans/kpis');
  return response.data as PlanKpis;
};

export const getPlanGenerationCheck = async (id: string): Promise<GenerationCheck> => {
  const response = await apiClient.get(`admin/plans/${id}/generation-check`);
  return response.data as GenerationCheck;
};

export const getPlan = async (id: string): Promise<PlanDetail> => {
  const response = await apiClient.get(`admin/plans/${id}`);
  return response.data as PlanDetail;
};

export const createPlan = async (payload: PlanPayload): Promise<PlanDetail> => {
  const response = await apiClient.post('admin/plans', payload);
  return response.data as PlanDetail;
};

export const updatePlan = async (id: string, payload: PlanPayload): Promise<PlanDetail> => {
  const response = await apiClient.put(`admin/plans/${id}`, payload);
  return response.data as PlanDetail;
};

export const deletePlan = async (id: string): Promise<void> => {
  await apiClient.delete(`admin/plans/${id}`);
};
